/**
 * MedListDraftsBlock — the medication lists saved for THIS client, on the
 * record's Documents tab.
 *
 * A Medication List draft linked to a client (from the record's "Make med
 * list" dialog, or from the tool with the client picked) is the CM's work
 * product about this client, so it is reachable from the client: each row
 * opens the list in the Medication List tool to continue, and downloads
 * the branded .docx of the saved list.
 *
 * Reads through the drafts inbox query narrowed to this client and the
 * med_list tool, under the same visibility model as the inbox (own drafts,
 * or all for the owner). Gated on use_ai_tools like the tool itself: a role
 * without it fires no request and sees nothing. Zero rows = the block does
 * not render (the tab stays uncluttered for the many clients without one).
 */

import { type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useTenant } from '@/lib/auth';
import { canUseAiTools } from '@/lib/capabilities';
import { useAiDrafts } from '@/lib/aiDrafts';
import { aiToolsApi } from '@/lib/api';
import { DownloadDocxButton } from '@/components/DownloadDocxButton';
import { DocumentPrintPdfButton } from '@/components/DocumentPrintPdfButton';
import { proseDate } from '@/lib/proseDate';

const GHOST_BTN_CLS =
  'inline-flex min-h-9 items-center justify-center rounded-pill px-3 text-xs font-semibold text-sage-800 hover:bg-sage-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400';

export interface MedListDraftsBlockProps {
  clientId: string;
}

export function MedListDraftsBlock({ clientId }: MedListDraftsBlockProps): ReactNode {
  const { tenantSlug, whoami } = useTenant();
  const eligible = canUseAiTools(whoami);
  const { drafts } = useAiDrafts(tenantSlug, {
    enabled: eligible,
    client_id: clientId,
    tool: 'med_list',
  });

  if (!eligible || drafts.length === 0) return null;

  return (
    <section
      className="rounded-card bg-paper shadow-sh1 border border-sage-800/[8%] p-6"
      data-testid="med-list-drafts-block"
    >
      <header className="flex items-center gap-2 mb-1">
        <h3 className="font-display text-lg text-sage-800">Medication lists</h3>
      </header>
      <ul className="divide-y divide-sage-800/[6%]">
        {drafts.map((d) => (
          <li
            key={d.id}
            className="flex flex-wrap items-center justify-between gap-2 py-2"
            data-testid={`med-list-draft-row-${d.id}`}
          >
            <Link
              to={`/t/${tenantSlug}/ai-tools/med-list?draft=${encodeURIComponent(d.id)}`}
              className="text-sm font-semibold text-sage-800 underline-offset-2 hover:underline"
            >
              Medication list, {proseDate(d.created_at.slice(0, 10))}
              {d.status === 'finalized' ? ' (finalized)' : ''}
            </Link>
            <span className="flex flex-wrap gap-2">
              <DownloadDocxButton
                url={aiToolsApi.medListDraftDownloadUrl(tenantSlug, d.id)}
                filename="Medication List.docx"
                className={GHOST_BTN_CLS}
                testId={`med-list-draft-download-${d.id}`}
              >
                Download .docx
              </DownloadDocxButton>
              <DocumentPrintPdfButton
                url={aiToolsApi.medListDraftDownloadUrl(tenantSlug, d.id)}
                filename="Medication List.docx"
                className={GHOST_BTN_CLS}
                testId={`med-list-draft-print-pdf-${d.id}`}
              />
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}
